// src/AirshipLoader.tsx
import { useEffect as useEffect4, useState as useState3 } from "react";

// src/hooks/useBrowserNotificationPermission.ts
import { useState, useEffect } from "react";
function useBrowserNotificationPermission(hasPushConsent) {
  const supportsNOtifications = typeof window !== "undefined" && "Notification" in window;
  const [permission, setPermission] = useState(
    supportsNOtifications ? Notification.permission : "default"
  );
  useEffect(() => {
    if (!supportsNOtifications) {
      setPermission("default");
      return;
    }
    setPermission(Notification.permission);
    if (hasPushConsent && Notification.permission === "default") {
      Notification.requestPermission().then(setPermission);
    }
  }, [hasPushConsent, supportsNOtifications]);
  return permission;
}

// src/hooks/useAirshipChannel.ts
import { useEffect as useEffect2, useRef, useState as useState2 } from "react";
function useAirshipChannel(hasPushConsent, notificationPermission, sdk) {
  const [isOptedIn, setIsOptedIn] = useState2(void 0);
  const isManagingChannel = useRef(false);
  useEffect2(() => {
    if (!sdk || isManagingChannel.current) return;
    const manageChannel = async () => {
      isManagingChannel.current = true;
      try {
        const optedIn = await sdk.channel.optedIn();
        const canRegister = await sdk.canRegister();
        if (hasPushConsent && notificationPermission === "granted" && canRegister && !optedIn) {
          await sdk.register();
        }
        if ((!hasPushConsent || notificationPermission === "denied") && optedIn) {
          await sdk.channel.optOut();
        }
        setIsOptedIn(await sdk.channel.optedIn());
      } catch (error) {
        console.error("[Airship] Failed to manage push channel:", error);
        setIsOptedIn(void 0);
      } finally {
        isManagingChannel.current = false;
      }
    };
    manageChannel();
  }, [sdk, hasPushConsent, notificationPermission]);
  return { isOptedIn };
}

// src/hooks/useAirshipPrivacy.ts
import { useEffect as useEffect3 } from "react";

// src/types.ts
var AirshipFeature = /* @__PURE__ */ ((AirshipFeature2) => {
  AirshipFeature2["Push"] = "push";
  AirshipFeature2["TagsAndAttributes"] = "tags_and_attributes";
  AirshipFeature2["Contacts"] = "contacts";
  AirshipFeature2["FeatureFlags"] = "feature_flags";
  AirshipFeature2["Analytics"] = "analytics";
  return AirshipFeature2;
})(AirshipFeature || {});
var ALL_FEATURES = Object.values(AirshipFeature);

// src/hooks/useAirshipPrivacy.ts
function useAirshipPrivacy(hasTrackingConsent, userDetails, sdk, isOptedIn) {
  useEffect3(() => {
    if (!sdk || !isOptedIn) return;
    const managePrivacy = async () => {
      const { privacyManager, contact } = sdk;
      const featuresToKeep = ["push" /* Push */, "contacts" /* Contacts */];
      const currentlyEnabled = await privacyManager.listEnabled();
      if (hasTrackingConsent) {
        await privacyManager.enable(...ALL_FEATURES);
      } else {
        const featuresToDisable = currentlyEnabled.filter(
          (f) => !featuresToKeep.includes(f)
        );
        if (featuresToDisable.length > 0) {
          await privacyManager.disable(
            ...featuresToDisable
          );
        }
      }
      const { userId, brand, businessType } = userDetails;
      const { isAnonymous } = await contact.identity();
      if (hasTrackingConsent && userId && isAnonymous) {
        const formattedId = formatUserId(userId, brand, businessType);
        await contact.identify(formattedId);
      } else if (!hasTrackingConsent && !isAnonymous) {
        await contact.reset();
      }
    };
    managePrivacy().catch((error) => {
      console.error("[Airship] Failed to manage privacy settings:", error);
    });
  }, [sdk, hasTrackingConsent, userDetails, isOptedIn]);
}
function formatUserId(userId, brand, businessType) {
  return `${businessType}_${brand}_${userId}`;
}

// src/loadAirshipScript.ts
var loadAirshipScript = ({
  sdkSrc,
  appKey,
  token,
  vapidPublicKey,
  scriptId = "_uasdk",
  workerUrl = "/airship-push-worker.js"
}) => {
  if (window == null ? void 0 : window.UA) {
    return window.UA;
  }
  const scriptString = `(function (n, r, e, t, c) {
      var i,
        o = "Promise" in n,
        u = {
          then: function () { return u; },
          catch: function (n) { return n(new Error("Airship SDK Error: Unsupported browser")), u; },
        },
        s = o
          ? new Promise(function (n, r) {
              i = function (e, t) { e ? r(e) : n(t); };
            })
          : u;
      s._async_setup = function (n) {
        if (o)
          try { i(null, n(c)); } catch (n) { i(n); }
      };
      n[t] = s;
      var a = r.createElement("script");
      a.src = e; a.async = !0; a.id = "${scriptId}"; a.rel = t;
      r.head.appendChild(a);
    })(window, document, '${sdkSrc}', 'UA', {
      vapidPublicKey: '${vapidPublicKey}',
      appKey: '${appKey}',
      token: '${token}',
      workerUrl: '${workerUrl}',
      enabledFeatures: ['push', 'contacts']
    });`;
  const scriptElement = document.createElement("script");
  scriptElement.type = "text/javascript";
  scriptElement.textContent = scriptString;
  document.head.appendChild(scriptElement);
  return window.UA;
};

// src/AirshipLoader.tsx
var AirshipLoader = (props) => {
  const {
    appKey,
    token,
    vapidPublicKey,
    sdkSrc,
    workerUrl,
    hasPushConsent,
    hasTrackingConsent,
    userId,
    brand,
    businessType
  } = props;
  const [sdk, setSdk] = useState3();
  const notificationPermission = useBrowserNotificationPermission(hasPushConsent);
  useEffect4(() => {
    if (!hasPushConsent || notificationPermission !== "granted") return;
    let isCancelled = false;
    const initialize = async () => {
      try {
        if (isCancelled) return;
        const sdkInstance = await loadAirshipScript({
          appKey,
          token,
          vapidPublicKey,
          sdkSrc,
          workerUrl
        });
        if (isCancelled) return;
        if (sdkInstance) {
          window.airshipSDK = sdkInstance;
          setSdk(sdkInstance);
        }
      } catch (error) {
        console.error("[Airship] Initialization process failed:", error);
      }
    };
    initialize();
    return () => {
      isCancelled = true;
    };
  }, [
    hasPushConsent,
    notificationPermission,
    appKey,
    token,
    vapidPublicKey,
    sdkSrc,
    workerUrl
  ]);
  const { isOptedIn } = useAirshipChannel(
    hasPushConsent,
    notificationPermission,
    sdk
  );
  useAirshipPrivacy(
    hasTrackingConsent,
    { brand, businessType, userId },
    sdk,
    isOptedIn
  );
  return null;
};
export {
  AirshipLoader
};
